import { PharmaStpFileModel } from "../../data-model/nycbsPharmaOrder";
import { API_ENDPOINTS } from "../../enums/apiUrls";
import { logDataToConsole, logError } from "../../utils/customLogger";
import { getOrgIdForFetchExternalWorklist } from "../../utils/organizationHelper";
import { getBearerToken } from "../postCall/bearerToken";

const getAuthHeaders = async (): Promise<Record<string, string>> => {
  const token = await getBearerToken();
  return {
    Authorization: `Bearer ${token}`,
  };
};

const createRequestOptions = (
  body?: Record<string, unknown>,
  header?: Record<string, string>,
  requestType: "POST" | "GET" = "POST",
): RequestInit => {
  const headers = new Headers();
  headers.append("Content-Type", "application/json");
  if (header) {
    for (const key in header) {
      headers.append(key, header[key]);
    }
  }

  const options: RequestInit = {
    method: requestType,
    headers,
    redirect: "follow",
  };

  if (requestType === "POST" && body) {
    options.body = JSON.stringify(body);
  }

  return options;
};

const handleApiResponse = async (response: Response): Promise<any> => {
  if (!response.ok) {
    throw new Error(`HTTP error! Status: ${response.status}`);
  }
  return await response.json();
};

const fetchApiData = async (
  url: string,
  logKey: string,
  body?: Record<string, unknown>,
  headers?: Record<string, string>,
  requestType: "POST" | "GET" = "POST",
): Promise<any> => {
  const requestOptions = createRequestOptions(body, headers ?? {}, requestType);

  try {
    const response = await fetch(url, requestOptions);
    const result = await handleApiResponse(response);
    logDataToConsole(logKey, result);
    return result;
  } catch (error) {
    logError(error as Error, `Error in ${logKey}`);
    throw error;
  }
};

export interface OrderByItem {
  column: string;
  direction: "ASC" | "DESC";
}

export interface FetchPharmaStpFileDataParams {
  date_from_filename_start?: string;
  date_from_filename_end?: string;
  filters?: {
    patient_mrn?: string[];
    [key: string]: any;
  };
  page?: number;
  page_size?: number;
  request_id?: string;
  order_by?: OrderByItem[];
}

export interface PharmaStpFileApiResponse {
  success: boolean;
  message: string;
  rows: PharmaStpFileModel[];
  row_count: number;
  page: number;
  page_size: number;
  total_pages: number;
  total_count: number;
}

/** @deprecated Use PharmaStpFileModel — kept for backward-compatible imports */
export type PharmaStpFileApiRow = PharmaStpFileModel;

export interface Meta {
  cmm_st_rpa_status: string | null;
  denial_letter_upload_status: string | null;
  denial_letter_upload_status_updated_at: string | null;
}

export const fetchPharmaStpFileData = async (
  params: FetchPharmaStpFileDataParams,
): Promise<PharmaStpFileApiResponse> => {
  const orgId = getOrgIdForFetchExternalWorklist();
  const body: Record<string, unknown> = {
    org_id: orgId ?? "",
    filters: params.filters ?? {},
    page: params.page ?? 1,
    request_id: params.request_id,
    order_by: params.order_by,
  };

  // Only add date range when callers pass it. Hooks omit dates when searching with no filter.
  if (params.date_from_filename_start !== undefined) {
    body.date_from_filename_start = params.date_from_filename_start;
  }
  if (params.date_from_filename_end !== undefined) {
    body.date_from_filename_end = params.date_from_filename_end;
  }

  // Only include page_size if it's explicitly provided
  if (params.page_size !== undefined) {
    body.page_size = params.page_size;
  }

  const headers = await getAuthHeaders();

  return await fetchApiData(
    API_ENDPOINTS.GET_PHARMA_STP_FILE_DATA,
    "fetchPharmaStpFileData",
    body,
    headers,
    "POST",
  );
};
